// approver-onboarding-modal/steps/CompletionStep.tsx
import React, { useEffect } from 'react'
import { Typography, Button } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'

import { useApproverOnboardingContext } from '../context'

import IntlMessages from '../../../util/IntlMessages'

import { FrontendUrls } from '../../../types/urls'

const { Title, Text } = Typography

interface CompletionStepProps {
  onClose?: () => void
  onBack?: () => void
}

export const CompletionStep: React.FC<CompletionStepProps> = ({ onClose, onBack }) => {
  const { markComplete, isLoading, currentStep } = useApproverOnboardingContext()
  const history = useHistory()

  useEffect(() => {
    // Mark as completed when this step is reached
    markComplete()
  }, [markComplete])

  const handleGoToDashboard = () => {
    onClose?.()
    setTimeout(() => {
      history.push(FrontendUrls.dashboard)
    }, 100)
  }

  const handleNavigationLinkClick = (url: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    onClose?.()
    setTimeout(() => {
      history.push(url)
    }, 100)
  }

  return (
    <div className="approver-onboarding-step completion-step">
      <div className="step-content">
        <div className="step-left-section">
          <div className="completion-content-container">
            {/* Back button */}
            {currentStep > 0 && <Button type="text" icon={<LeftOutlined />} onClick={onBack} className="step-back-button" />}

            <Title level={2} className="completion-title">
              <IntlMessages id="approver.onboarding.completion.title" />
            </Title>

            <Text className="completion-description">
              <IntlMessages id="approver.onboarding.completion.description" />
            </Text>

            {/* Action items */}
            <div className="completion-actions">
              <div className="completion-action-item">
                <span className="completion-action-icon">👀</span>
                <Text>
                  <IntlMessages
                    id="approver.onboarding.completion.helpfulLinks.dashboard"
                    values={{
                      link: (...chunks) => (
                        <a href={FrontendUrls.dashboard} onClick={handleNavigationLinkClick(FrontendUrls.dashboard)} className="completion-action-link">
                          <u>{chunks}</u>
                        </a>
                      ),
                    }}
                  />
                </Text>
              </div>
              <div className="completion-action-item">
                <span className="completion-action-icon">📅</span>
                <Text>
                  <IntlMessages
                    id="approver.onboarding.completion.helpfulLinks.wallchart"
                    values={{
                      link: (...chunks) => (
                        <a href={FrontendUrls.wallchart} onClick={handleNavigationLinkClick(FrontendUrls.wallchart)} className="completion-action-link">
                          <u>{chunks}</u>
                        </a>
                      ),
                    }}
                  />
                  <IntlMessages id="app.and" />
                  &nbsp;
                  <IntlMessages
                    id="approver.onboarding.completion.helpfulLinks.calendar"
                    values={{
                      link: (...chunks) => (
                        <a href={FrontendUrls.calendarPage} onClick={handleNavigationLinkClick(FrontendUrls.calendarPage)} className="completion-action-link">
                          <u>{chunks}</u>
                        </a>
                      ),
                    }}
                  />
                </Text>
              </div>
              <div className="completion-action-item">
                <span className="completion-action-icon">💬</span>
                <Text>
                  <IntlMessages
                    id="approver.onboarding.completion.helpfulLinks.notifications"
                    values={{
                      link: (...chunks) => (
                        <a href={FrontendUrls.notifications} onClick={handleNavigationLinkClick(FrontendUrls.notifications)} className="completion-action-link">
                          <u>{chunks}</u>
                        </a>
                      ),
                    }}
                  />
                </Text>
              </div>
            </div>

            <Text className="completion-team-help">
              <IntlMessages id="approver.onboarding.completion.teamHelp" />
            </Text>

            <Button type="primary" size="large" onClick={handleGoToDashboard} loading={isLoading} className="completion-primary-button">
              <IntlMessages id="app.goToDashboard" />
            </Button>
          </div>
        </div>

        <div className="step-right-section">
          <div className="completion-illustration-container">
            <img src="/approver-onboarding/Completion.svg" alt="Completion illustration" className="completion-illustration" />
          </div>
        </div>
      </div>
    </div>
  )
}
